<script setup>
import { defineAsyncComponent, onMounted, ref, useTemplateRef } from 'vue'
import { attr_gec_subject_vat, attr_gec_suppliers, attr_subject_fiscal_codes } from '@/services/compiledPollService.js'
import { searchSelect } from '@/libs/utils.js'

import TranslationProvider from '@/provider/translations.js'
import { useFiltersStore } from '@/services/store.js'
import { library } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faFilter, faInfoCircle } from '@digitalpa/pro-solid-svg-icons'
import ConfigurationProvider from '@/provider/configuration.js'

library.add(faFilter, faInfoCircle)

const props = defineProps({
  isLoading: Boolean,
  currentFilters: {
    default: '',
    type: String
  },
  workflowList: {
    default: []
  },
  statesList: {
    default: []
  }
})

let CustomFilters = null
if (ConfigurationProvider.getConfig()['app']['filters']['additional_filters_component']) {
  const folder = ConfigurationProvider.getConfig()['app']['filters']['additional_filters_component']['folder']
  const name = ConfigurationProvider.getConfig()['app']['filters']['additional_filters_component']['name']
  CustomFilters = defineAsyncComponent(() =>
    import(`@/components/${folder}/${name}.vue`)
  )
}
const customFilters = useTemplateRef('custom-filters')
const filtersStore = useFiltersStore()

const emits = defineEmits(['applicaFiltri', 'showCardChanged'])

const disabledResettaFiltri = ref(true)
const showCard = ref(true)
const loadingFilters = ref(true)
const activeFilters = ref(0)
const emptyAttributes = ref([])
const suppliersList = ref([])
const pIvaList = ref([])
const CFList = ref([])
const currentSuppliersSearch = ref('')
const currentPivaSearch = ref('')
const currentCFSearch = ref('')

const errorFilters = ref({
  enabled: false,
  message: ''
})

onMounted(async () => {
  loadingFilters.value = true
  errorFilters.value.enabled = false
  errorFilters.value.message = ''
  resettaFiltri()
  showCard.value = await filtersStore.getShowCard()
  if (ConfigurationProvider.getConfig()['app']['filters']['empty_attributes']) {
    for (const emptyAttr of ConfigurationProvider.getConfig()['app']['filters']['empty_attributes']) {
      emptyAttributes.value.push({
        label: TranslationProvider.getTrans(emptyAttr.label),
        value: emptyAttr.value
      })
    }
  }
  countActiveFilters()
  loadingFilters.value = false
  checkDisabledResettaFiltri()
})

function checkDisabledResettaFiltri() {
  disabledResettaFiltri.value = true  // TODO - refactor con filterStore
  if (Object.keys(filtersStore.filters).length) {
    Object.values(filtersStore.filters).forEach(filter => {
      if (filter) {
        disabledResettaFiltri.value = false
      }
    })
  }
}

function resettaFiltri() {
  errorFilters.value.enabled = false
  errorFilters.value.message = ''
  filtersStore.setFilters({})
  if (customFilters.value) {
    customFilters.value.resetFilters()
  }

  checkDisabledResettaFiltri()
  applicaFiltri()
}

function applicaFiltri() {
  errorFilters.value.enabled = false
  errorFilters.value.message = ''
  checkDisabledResettaFiltri()
  if (filtersStore.filters['state']?.length && filtersStore.filters['exclude_states']?.length) {
    errorFilters.value.message = TranslationProvider.getTrans('app.error_filters.excludeStates_with_state_filter')
    errorFilters.value.enabled = true
    return
  }
  emits('applicaFiltri')
  countActiveFilters()
}

const showHideFilters = () => {
  showCard.value = !showCard.value

  emits('showCardChanged', showCard.value)

  filtersStore.setShowCard(showCard.value)
}

const countActiveFilters = () => {
  activeFilters.value = 0
  const filters = filtersStore.filters
  for (const [index, entry] of Object.entries(filters)) {
    if (index !== 'attribute_filters' && (Array.isArray(entry) || typeof entry !== 'object') && entry !== undefined && entry !== null && entry) {
      if ((Array.isArray(entry) && !entry.length) || (typeof entry == 'object' && !Object.values(entry).length)) {
        continue
      }
      activeFilters.value += 1

    }
  }
}
</script>

<template>
  <div
    class="col-12 p-3 mb-3  border-bottom"
    @keydown.enter="applicaFiltri"
  >
    <div class="container-fluid">

      <div class="row mb-3">
        <div class="col-12 text-end"
             v-if="
            ConfigurationProvider.getConfig()['app']['filters']['default_enabled'] ||
            ConfigurationProvider.getConfig()['app']['filters']['additional_filters_component']
          "
        >
          <button :disabled="loadingFilters" class="btn btn-sm btn-primary" @click="showHideFilters">
            <span v-if="loadingFilters" class="spinner-border spinner-border-sm" aria-hidden="true"></span>
            <span v-if="loadingFilters" class="visually-hidden" role="status">Loading...</span>
            <span v-else class="fw-bold">
              <span v-if="activeFilters" class="badge rounded-pill bg-white text-primary">
                {{ activeFilters }}
              </span>
              {{ TranslationProvider.getTrans('app.filters') }}
              <font-awesome-icon class="ms-2" :icon="'fa-filter'"></font-awesome-icon>
            </span>
          </button>
        </div>
      </div>
      <div :class="'row collapse ' + (showCard && !loadingFilters ? 'show' : '')">
        <div class="col-12 px-1" v-if="errorFilters.enabled">
          <div class="alert alert-danger px-2 py-2" v-html="errorFilters.message" />
        </div>
        <template v-if="ConfigurationProvider.getConfig()['app']['filters']['default_enabled']">

          <div class="col-12 col-lg-3 px-2"
               v-if="!ConfigurationProvider.getConfig()['app']['filters']['exclude_defaults'].includes('fornitore')"
          >
            <div class="mb-2">
              <label for="fornitore" class="form-label">
                {{ TranslationProvider.getTrans('app.fornitore') }}
              </label>

              <v-select
                id="rating_evaluation_approver"
                @search="async (searchQuery, loading) => {
                 currentSuppliersSearch = searchQuery;
                 suppliersList = await searchSelect(
                    searchQuery,
                    loading,
                    attr_gec_suppliers,
                  )
                }"
                v-model="filtersStore.filters['gec_suppliers']"
                :options="suppliersList"
                :filterable="false"
                @search:blur="() => {suppliersList = []}"
                @search:selected="() => {suppliersList = []}"
                :placeholder="TranslationProvider.getTrans('app.fornitore')"
                clearable
              >
                <template v-slot:no-options>
                    <span v-if="currentSuppliersSearch.length >= 3">
                        {{ TranslationProvider.getTrans('app.no_result_found') }}
                    </span>
                  <span v-else>
                       {{ TranslationProvider.getTrans('app.digita_per_cercare') }}
                    </span>
                </template>
              </v-select>
              <small class="form-text">
                {{ TranslationProvider.getTrans('app.select2_min_chars') }}
              </small>
            </div>
          </div>

          <div class="col-12 col-lg-3 px-2"
               v-if="!ConfigurationProvider.getConfig()['app']['filters']['exclude_defaults'].includes('partita_iva')"
          >
            <div class="mb-2">
              <label for="partita_iva" class="form-label">
                {{ TranslationProvider.getTrans('app.partita_iva') }}
              </label>

              <v-select
                id="partita_iva_filter"
                @search="async (searchQuery, loading) => {
                   currentPivaSearch = searchQuery;
                   pIvaList = await searchSelect(
                      searchQuery,
                      loading,
                      attr_gec_subject_vat,
                    )
                }"
                v-model="filtersStore.filters['gec_subject_vat']"
                :options="pIvaList"
                :filterable="false"
                @search:blur="() => {pIvaList = []}"
                @search:selected="() => {pIvaList = []}"
                :placeholder="TranslationProvider.getTrans('app.partita_iva')"
                clearable
              >
                <template v-slot:no-options>
                    <span v-if="currentPivaSearch.length >= 3">
                        {{ TranslationProvider.getTrans('app.no_result_found') }}
                    </span>
                  <span v-else>
                       {{ TranslationProvider.getTrans('app.digita_per_cercare') }}
                    </span>
                </template>
              </v-select>
              <small class="form-text">
                {{ TranslationProvider.getTrans('app.select2_min_chars') }}
              </small>
            </div>
          </div>

          <div class="col-12 col-lg-3 px-2"
               v-if="!ConfigurationProvider.getConfig()['app']['filters']['exclude_defaults'].includes('partita_iva')"
          >
            <div class="mb-2">
              <label for="cf_filter" class="form-label">
                {{ TranslationProvider.getTrans('app.codice_fiscale') }}
              </label>

              <v-select
                id="cf_filter"
                @search="async (searchQuery, loading) => {
                   currentCFSearch = searchQuery;
                   CFList = await searchSelect(
                      searchQuery,
                      loading,
                      attr_subject_fiscal_codes,
                    )
                }"
                v-model="filtersStore.filters['gec_subject_fiscal_codes']"
                :options="CFList"
                :filterable="false"
                @search:blur="() => {CFList = []}"
                @search:selected="() => {CFList = []}"
                :placeholder="TranslationProvider.getTrans('app.codice_fiscale')"
                clearable
              >
                <template v-slot:no-options>
                    <span v-if="currentCFSearch.length >= 3">
                        {{ TranslationProvider.getTrans('app.no_result_found') }}
                    </span>
                  <span v-else>
                       {{ TranslationProvider.getTrans('app.digita_per_cercare') }}
                    </span>
                </template>
              </v-select>
              <small class="form-text">
                {{ TranslationProvider.getTrans('app.select2_min_chars') }}
              </small>
            </div>
          </div>

          <div class="col-12 col-lg-3 px-2"
               v-if="!ConfigurationProvider.getConfig()['app']['filters']['exclude_defaults'].includes('titoloCampagna')"
          >
            <div class="mb-2">
              <label for="descrizione_risorsa_standard" class="form-label">
                {{ TranslationProvider.getTrans('app.campagna') }}
              </label>
              <input
                id="titoloCampagna"
                v-model="filtersStore.filters['titolo_campagna']"
                type="text"
                class="form-control form-control-sm"
                :placeholder="TranslationProvider.getTrans('app.campagna')"
              />
            </div>
          </div>

          <div class="col-12 col-lg-3 px-2"
               v-if="!ConfigurationProvider.getConfig()['app']['filters']['exclude_defaults'].includes('cod_commessa')"
          >
            <div class="mb-2">
              <label for="cod_commessa" class="form-label">
                {{ TranslationProvider.getTrans('app.codice_commessa') }}
              </label>
              <input
                id="cod_commessa"
                v-model="filtersStore.filters['gec_order_uuid']"
                type="text"
                class="form-control form-control-sm"
                :placeholder="TranslationProvider.getTrans('app.codice_commessa')"
              />
            </div>
          </div>

          <div class="col-12 col-lg-3 px-2"
               v-if="!ConfigurationProvider.getConfig()['app']['filters']['exclude_defaults'].includes('cod_contratto')"
          >
            <div class="mb-2">
              <label for="cod_contratto" class="form-label">
                {{ TranslationProvider.getTrans('app.codice_contratto') }}
              </label>
              <input
                id="cod_contratto"
                v-model="filtersStore.filters['gec_uuid']"
                type="text"
                class="form-control form-control-sm"
                :placeholder="TranslationProvider.getTrans('app.codice_contratto')"
              />
            </div>
          </div>

          <div class="col-12 col-lg-3 px-2"
               v-if="!ConfigurationProvider.getConfig()['app']['filters']['exclude_defaults'].includes('state')"
          >
            <div class="mb-2">
              <label for="state" class="form-label">
                {{ TranslationProvider.getTrans('app.stato_somministrazione') }}
              </label>
              <v-select
                id="state"
                v-model="filtersStore.filters['state']"
                :options="statesList"
                :reduce="state => state.name"
                label="label"
                :placeholder="TranslationProvider.getTrans('app.stato_somministrazione')"
              >
                <template #no-options>
                  <div style="opacity: 0.8">{{ TranslationProvider.getTrans('app.no_result_found') }}</div>
                </template>
              </v-select>
            </div>
          </div>

          <div class="col-12 col-lg-3 px-2"
               v-if="ConfigurationProvider.getConfig()['app']['filters']['exclude_states']"
          >
            <div class="mb-2">
              <label for="state" class="form-label">
                {{ TranslationProvider.getTrans('app.questionari_da_escludere') }}
              </label>
              <v-select
                id="state"
                v-model="filtersStore.filters['exclude_states']"
                :options="statesList"
                :reduce="state => state.name"
                label="label"
                multiple
                :clearSearchOnBlur="() => true"
                :placeholder="TranslationProvider.getTrans('app.questionari_da_escludere')"
              >
                <template #no-options>
                  <div style="opacity: 0.8">{{ TranslationProvider.getTrans('app.no_result_found') }}</div>
                </template>
              </v-select>
            </div>
          </div>

          <div class="col-12 col-lg-6 px-2"
               v-if="!ConfigurationProvider.getConfig()['app']['filters']['exclude_defaults'].includes('titolo_contratto')"
          >
            <div class="mb-2">
              <label for="titolo_contratto" class="form-label">
                {{ TranslationProvider.getTrans('app.titolo_contratto') }}
              </label>
              <input
                id="titolo_contratto"
                v-model="filtersStore.filters['gec_contract_object']"
                type="text"
                class="form-control form-control-sm"
                :placeholder="TranslationProvider.getTrans('app.titolo_contratto')"
              />
            </div>
          </div>

          <div class="col-12 col-lg-3 px-2"
               v-if="!ConfigurationProvider.getConfig()['app']['filters']['exclude_defaults'].includes('empty_attribute') && emptyAttributes.length"
          >
            <div class="mb-2">
              <label for="empty_attribute" class="form-label">
                {{ TranslationProvider.getTrans('app.empty_attribute') }}
              </label>
              <v-select
                id="empty_attribute"
                v-model="filtersStore.filters['empty_attribute']"
                :options="emptyAttributes"
                :reduce="attr => attr.value"
                :placeholder="TranslationProvider.getTrans('app.empty_attribute')"
                multiple
                clearable
                :clearSearchOnBlur="() => true"
              >
                <template #no-options>
                  <div style="opacity: 0.8">{{ TranslationProvider.getTrans('app.no_result_found') }}</div>
                </template>
              </v-select>
            </div>
          </div>

          <div class="col-12 col-lg-3 px-2"
               v-if="!ConfigurationProvider.getConfig()['app']['filters']['exclude_defaults'].includes('workflow')"
          >
            <div class="mb-2">
              <label for="empty_attribute" class="form-label">
                {{ TranslationProvider.getTrans('app.workflow') }}
              </label>
              <v-select
                id="workflow"
                v-model="filtersStore.filters['workflow']"
                :options="workflowList"
                :reduce="attr => attr.key"
                :placeholder="TranslationProvider.getTrans('app.workflow')"
                label="label"
                clearable
                :clearSearchOnBlur="() => true"
              >
                <template #no-options>
                  <div style="opacity: 0.8">{{ TranslationProvider.getTrans('app.no_result_found') }}</div>
                </template>
              </v-select>
            </div>
          </div>
        </template>

        <component
          v-if="ConfigurationProvider.getConfig()['app']['filters']['additional_filters_component']"
          :is="CustomFilters"
          ref="custom-filters"
        >
        </component>
      </div>


      <div class="col-12 d-flex justify-content-end mt-2 px-2">
        <button
          class="btn btn-secondary btn-sm mx-1"
          :disabled="disabledResettaFiltri"
          @click="resettaFiltri"
        >
          {{ TranslationProvider.getTrans('app.reset_filters') }}
        </button>
        <button
          class="btn btn-primary btn-sm mx-1"
          :disabled="isLoading"
          @click="applicaFiltri"
        >
          {{ TranslationProvider.getTrans('app.apply_filters') }}
        </button>
      </div>
    </div>

  </div>
</template>

<style lang="scss">
.container-fluid .row {

  & .vs__search, .vs__selected {
    font-size: 0.875rem !important;
    margin: 2px 3px 2px 4px !important;
  }

  & .vs__search::placeholder {
    opacity: 0.8 !important;
  }

  & .vs__clear {
    font-size: 0.7rem !important;
  }
}
</style>
